import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, LogOut, UploadCloud, Loader2, RefreshCw, Link2, Check, X, Search } from "lucide-react";

const STATUS_META = {
  confirmed: { label: "Bevestigd", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  suggested: { label: "Voorstel", cls: "bg-blue-100 text-blue-800 border-blue-200" },
  review: { label: "Nakijken", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  unmapped: { label: "Niet gekoppeld", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  ignored: { label: "Genegeerd", cls: "bg-slate-100 text-slate-500 border-slate-200 line-through" },
};

const TABS = [
  { id: "todo", label: "Te koppelen" },
  { id: "review", label: "Nakijken" },
  { id: "suggested", label: "Voorstellen" },
  { id: "confirmed", label: "Bevestigd" },
  { id: "all", label: "Alle" },
];

export default function PurchasesMappings() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const fileInput = useRef(null);
  const [data, setData] = useState(null);
  const [assortment, setAssortment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [tab, setTab] = useState("todo");
  const [picker, setPicker] = useState(null); // mapping row for full-assortment picker
  const [pickerSearch, setPickerSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [m, a] = await Promise.all([
        api.get("/purchases/mappings"),
        api.get("/purchases/colruyt/products"),
      ]);
      setData(m.data);
      setAssortment(a.data.products || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (fileInput.current) fileInput.current.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const r = await api.post("/purchases/colruyt/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Assortiment ingeladen: ${r.data.assortment_count} artikelen`);
      if (r.data.errors?.length) toast.warning(r.data.errors.join(" · "));
      await rebuild(true);
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const rebuild = async (silent = false) => {
    setRebuilding(true);
    try {
      const r = await api.post("/purchases/mappings/rebuild");
      if (!silent) toast.success(`Voorstellen herberekend voor ${r.data.vtec_products} producten`);
      if (!silent) await load();
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setRebuilding(false); }
  };

  const [confirming, setConfirming] = useState(false);
  const confirmAllSuggested = async () => {
    setConfirming(true);
    try {
      const r = await api.post("/purchases/mappings/confirm-suggested");
      toast.success(`${r.data.confirmed} voorstel(len) bevestigd`);
      await load();
      setTab("review");
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setConfirming(false); }
  };

  const setMapping = async (row, art_nr, status) => {
    try {
      await api.put(`/purchases/mappings/${encodeURIComponent(row.key)}`, { colruyt_art_nr: art_nr, status });
      // optimistic local update
      setData((d) => {
        const cp = assortment.find((x) => x.art_nr === art_nr) || null;
        const mappings = d.mappings.map((m) => m.key === row.key
          ? { ...m, status: status === "confirmed" ? "confirmed" : status, colruyt_art_nr: status === "confirmed" ? art_nr : null, colruyt: status === "confirmed" ? cp : null }
          : m);
        return { ...d, mappings };
      });
      setPicker(null);
      toast.success(status === "confirmed" ? "Koppeling bevestigd" : status === "ignored" ? "Genegeerd" : "Koppeling gewist");
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const counts = data?.counts || {};
  const assortmentLoaded = data?.assortment_loaded || 0;

  const rows = useMemo(() => {
    const all = data?.mappings || [];
    if (tab === "all") return all;
    if (tab === "todo") return all.filter((m) => m.status === "review" || m.status === "unmapped" || m.status === "suggested");
    return all.filter((m) => m.status === tab);
  }, [data, tab]);

  const pickerResults = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    let list = assortment;
    if (q) list = assortment.filter((p) => (`${p.name} ${p.art_nr} ${p.packaging}`).toLowerCase().includes(q));
    return list.slice(0, 60);
  }, [assortment, pickerSearch]);

  return (
    <div className="min-h-screen bg-background" data-testid="purchases-mappings">
      <header className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button variant="ghost" size="sm" data-testid="mappings-back" onClick={() => navigate("/purchases")}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Winkels
          </Button>
          <Button variant="ghost" size="sm" onClick={() => { logout(); navigate("/"); }}>
            <LogOut className="h-4 w-4 mr-2" /> Afmelden
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-1.5 rounded-md bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700">
              <Link2 className="h-3.5 w-3.5" /> Koppelingen VTEC ↔ Colruyt
            </div>
            <h1 className="font-heading text-3xl font-semibold tracking-tight mt-1">Productkoppelingen</h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              Het systeem stelt automatisch een Colruyt-artikel voor per VTEC-product (merk + maat).
              Bevestig elke koppeling één keer — daarna wordt ze onthouden. <b>Er wordt niet gegokt:</b> bij twijfel kies je zelf.
            </p>
          </div>
          {user?.role === "admin" && (
            <div className="flex flex-col items-end gap-2">
              <input ref={fileInput} type="file" accept=".pdf" multiple className="hidden" data-testid="colruyt-file-input" onChange={onUpload} />
              <Button variant="outline" size="sm" disabled={uploading} data-testid="colruyt-upload-btn" onClick={() => fileInput.current?.click()}>
                {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UploadCloud className="h-4 w-4 mr-2" />} Colruyt-assortiment (PDF)
              </Button>
              <Button variant="ghost" size="sm" disabled={rebuilding || !assortmentLoaded} data-testid="rebuild-btn" onClick={() => rebuild(false)}>
                {rebuilding ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />} Voorstellen herberekenen
              </Button>
            </div>
          )}
        </div>

        {assortmentLoaded === 0 && (
          <div className="mt-5 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="no-assortment-warning">
            Nog geen Colruyt-assortiment ingeladen. {user?.role === "admin" ? "Upload de 3 bestel-PDF's hierboven." : "Vraag een beheerder om het assortiment in te laden."}
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5 mt-6" data-testid="mapping-counts">
          {[["confirmed", "Bevestigd"], ["suggested", "Voorstel"], ["review", "Nakijken"], ["unmapped", "Niet gekoppeld"], ["ignored", "Genegeerd"]].map(([k, lbl]) => (
            <div key={k} className="rounded-lg border bg-slate-50 px-3 py-2.5">
              <div className="text-xs font-medium text-slate-600">{lbl}</div>
              <div className="text-xl font-semibold tabular-nums text-slate-800">{counts[k] || 0}</div>
            </div>
          ))}
        </div>

        {(counts.suggested || 0) > 0 && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3" data-testid="confirm-suggested-bar">
            <div className="text-sm text-blue-900">
              <b>{counts.suggested}</b> zeker voorstel(len) klaar om te bevestigen (uniek merk + juiste maat). De <b>{counts.review || 0}</b> nakijken-gevallen kies je hierna zelf.
            </div>
            <Button size="sm" disabled={confirming} data-testid="confirm-suggested-btn" onClick={confirmAllSuggested} className="bg-blue-600 hover:bg-blue-700">
              {confirming ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />} Bevestig alle {counts.suggested} voorstellen
            </Button>
          </div>
        )}

        <div className="flex flex-wrap gap-1.5 mt-6 border-b">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`mapping-tab-${t.id}`} onClick={() => setTab(t.id)}
              className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.id ? "border-amber-600 text-amber-700" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-muted-foreground mt-8">Laden…</p>
        ) : (
          <div className="mt-4 space-y-2.5" data-testid="mapping-list">
            {rows.length === 0 ? (
              <p className="text-muted-foreground py-10 text-center">Geen producten in deze weergave.</p>
            ) : rows.map((m) => (
              <div key={m.key} data-testid={`mapping-row-${m.key}`} className="rounded-lg border bg-white p-3.5">
                <div className="flex flex-wrap items-center gap-3 justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{m.vtec_name}</span>
                      <Badge variant="outline" className={STATUS_META[m.status]?.cls}>{STATUS_META[m.status]?.label}</Badge>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">Ident {m.ident_number} · Bestelnr {m.order_number}</div>
                    {m.colruyt && (
                      <div className="text-sm text-emerald-700 mt-1 flex items-center gap-1.5" data-testid={`mapping-confirmed-${m.key}`}>
                        <Check className="h-3.5 w-3.5" /> {m.colruyt.name} <span className="text-muted-foreground">({m.colruyt.art_nr} · {m.colruyt.packaging})</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {m.status !== "ignored" && (
                      <Button variant="ghost" size="sm" data-testid={`mapping-ignore-${m.key}`} onClick={() => setMapping(m, null, "ignored")} title="Negeren (geen bestelling)">
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                    <Button variant="outline" size="sm" data-testid={`mapping-pick-${m.key}`} onClick={() => { setPicker(m); setPickerSearch(""); }}>
                      <Search className="h-4 w-4 mr-1.5" /> Kies artikel
                    </Button>
                  </div>
                </div>

                {m.status !== "confirmed" && m.candidates?.length > 0 && (
                  <div className="mt-2.5 flex flex-wrap gap-1.5" data-testid={`mapping-candidates-${m.key}`}>
                    {m.candidates.map((c) => (
                      <button key={c.art_nr} data-testid={`candidate-${m.key}-${c.art_nr}`} onClick={() => setMapping(m, c.art_nr, "confirmed")}
                        className={`text-left rounded-md border px-2.5 py-1.5 text-xs hover:border-emerald-500 hover:bg-emerald-50 transition-colors ${c.art_nr === m.suggested_art_nr ? "border-blue-300 bg-blue-50" : "border-slate-200"}`}>
                        <div className="font-medium">{c.name}</div>
                        <div className="text-muted-foreground">{c.art_nr} · {c.packaging}{c.art_nr === m.suggested_art_nr ? " · voorstel" : ""}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {user?.role === "admin" && <ConfirmationTester />}
      </main>

      <Dialog open={!!picker} onOpenChange={(o) => !o && setPicker(null)}>
        <DialogContent className="max-w-lg" data-testid="picker-dialog">
          <DialogHeader><DialogTitle>Kies Colruyt-artikel</DialogTitle></DialogHeader>
          {picker && <p className="text-sm text-muted-foreground -mt-2">Voor VTEC-product <b>{picker.vtec_name}</b></p>}
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input autoFocus placeholder="Zoek op naam, art.nr of verpakking…" className="pl-8" data-testid="picker-search"
              value={pickerSearch} onChange={(e) => setPickerSearch(e.target.value)} />
          </div>
          <div className="max-h-[50vh] overflow-y-auto space-y-1 -mx-1 px-1">
            {pickerResults.map((p) => (
              <button key={p.art_nr} data-testid={`picker-opt-${p.art_nr}`} onClick={() => picker && setMapping(picker, p.art_nr, "confirmed")}
                className="w-full text-left rounded-md border border-slate-200 px-3 py-2 text-sm hover:border-emerald-500 hover:bg-emerald-50 transition-colors">
                <div className="font-medium">{p.name}</div>
                <div className="text-xs text-muted-foreground">{p.art_nr} · {p.category} · {p.packaging}</div>
              </button>
            ))}
            {pickerResults.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">Geen resultaten.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function ConfirmationTester() {
  const [text, setText] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async (send) => {
    if (!text.trim()) { toast.warning("Plak eerst de tekst van de Colruyt-mail."); return; }
    setBusy(true);
    try {
      const r = await api.post("/purchases/confirmations/test", { text, send });
      setRes(r.data);
      if (send && r.data.sent) toast.success(`Alarm verstuurd naar ${r.data.alert_recipient}`);
      else if (send) toast.warning("Geen niet-voorraadartikelen gevonden — niets verstuurd.");
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-10 rounded-lg border bg-slate-50 p-5" data-testid="confirmation-tester">
      <h3 className="font-heading text-sm font-semibold text-slate-700 uppercase tracking-wide">Colruyt-bevestiging testen (Fase 9)</h3>
      <p className="text-xs text-muted-foreground mt-1 max-w-2xl">Plak de tekst van een Colruyt-bevestigingsmail. Het systeem herkent de sectie "Niet-voorraadartikelen", koppelt de artikelen aan je verzonden bestelling (naam + gewenste hoeveelheid + winkel) en kan het alarm sturen naar harjeetghotra20@gmail.com. In productie gebeurt dit automatisch.</p>
      <textarea data-testid="conf-text" value={text} onChange={(e) => setText(e.target.value)} rows={6}
        placeholder="Beste professional, ... Niet-voorraadartikelen : 14650 B&J WICH ..." 
        className="mt-3 w-full rounded-md border border-slate-300 p-2.5 text-sm font-mono" />
      <div className="flex items-center gap-2 mt-2">
        <Button size="sm" variant="outline" disabled={busy} data-testid="conf-analyze" onClick={() => run(false)}>
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Analyseren
        </Button>
        <Button size="sm" disabled={busy || !res?.matched?.length} className="bg-amber-600 hover:bg-amber-700" data-testid="conf-send" onClick={() => run(true)}>
          Alarm nu versturen
        </Button>
      </div>
      {res && (
        <div className="mt-3 text-sm" data-testid="conf-result">
          {res.is_confirmation ? (
            <>
              <div className="text-muted-foreground">{res.out_of_stock_count} niet-voorraadartikel(en) herkend.</div>
              {res.matched.map((m, i) => (
                <div key={i} className="mt-1"><b>{m.name}</b> — gewenst: {m.qty ?? "?"} · winkel: {m.store_name || "onbekend (geen recente bestelling)"}</div>
              ))}
              {res.matched.length > 0 && (
                <pre className="mt-3 whitespace-pre-wrap rounded border bg-white p-3 text-xs">{res.preview_body}</pre>
              )}
            </>
          ) : <div className="text-amber-700">Dit lijkt geen Colruyt-bevestigingsmail (geen "Niet-voorraadartikelen"/"Bestelde goederen").</div>}
        </div>
      )}
    </div>
  );
}
